import { tools, findTool, type ToolMeta } from './tools/registry';
import {
  clearSelectedFiles,
  peekSelectedFiles,
  setPendingFiles,
  setSelectedFiles,
  routeFiles,
} from './lib/pending-files';
import { getTheme, setTheme } from './lib/theme';
import { homeSeoPages, privacySeoPages, seoRoutePages, type HomeSeoPage, type PrivacySeoPage, type SeoAlternate, type SeoRoutePage } from './generated/seo-pages-lite';
import type { SeoPage } from './generated/seo-pages';
import { DEFAULT_LANG, detectDeviceLanguage, getStoredLanguage, languageDirection, matchSupportedLanguage, setStoredLanguage } from './i18n/registry';
import { uiCopy, type UiCopy } from './i18n/ui-copy';
import {
  getAnalyticsConsent,
  initMetrika,
  isMetrikaHost,
  setAnalyticsConsent,
  trackFileSelected,
  trackPageView,
  trackToolError,
} from './lib/metrika';

const BRAND_NAME = 'PDF911';
const SITE_ORIGIN = 'https://pdf911.com';
const DEFAULT_HOME = homeSeoPages.find((page) => page.lang === DEFAULT_LANG) ?? homeSeoPages[0];
const BOT_USER_AGENT_RE = /(bot|crawler|spider|crawling|googlebot|bingbot|duckduckbot|baiduspider|yandex|facebookexternalhit|twitterbot|slurp|semrush|ahrefs|mj12bot)/i;
const LANGUAGE_SELECTOR_ORDER = [
  'en-US',
  'es',
  'de',
  'fr',
  'pt-BR',
  'ja',
  'it',
  'nl',
  'pl',
  'tr',
  'id',
  'vi',
  'ko',
  'ar',
  'hi',
  'zh-CN',
  'ru',
];

type BeforeInstallPromptChoice = {
  outcome: 'accepted' | 'dismissed';
  platform: string;
};

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<BeforeInstallPromptChoice>;
};

let deferredInstallPrompt: BeforeInstallPromptEvent | null = null;



export function mountApp(root: HTMLElement) {
  applyPreferredLanguageRoute();
  const copy = uiCopy();
  const homePath = homePathForLanguage(currentLanguage());

  root.innerHTML = `
    <div class="min-h-screen flex flex-col">
      <header class="bg-slate-900/80 backdrop-blur sticky top-0 z-10">
        <div class="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <a href="${homePath}" class="flex items-center gap-2 font-bold text-lg" data-home-link>
            <span class="inline-block w-8 h-8 rounded-lg bg-brand text-white text-xs font-extrabold grid place-items-center">911</span>
            <span>${BRAND_NAME}</span>
          </a>
          <div class="flex items-center gap-2">
            <select class="language-select" data-language-select aria-label="${escapeHtml(copy.languageLabel)}">
              ${languageSelectorPages().map((page) => `<option value="${escapeHtml(page.lang)}">${escapeHtml(page.languageName)}</option>`).join('')}
            </select>
            <button type="button" class="install-button" data-install-app aria-label="${escapeHtml(copy.install.saveHint)}" title="${escapeHtml(copy.install.saveHint)}">
              <span class="install-button-icon" aria-hidden="true">${downloadIconSvg()}</span>
              <span class="install-button-label" data-install-label>${escapeHtml(copy.install.save)}</span>
            </button>
          </div>
        </div>
      </header>
      <main id="view" class="flex-1 w-full"></main>
      <footer id="app-footer" class="border-t border-slate-800 mt-12">
        <div class="max-w-6xl mx-auto px-4 py-4 text-xs text-slate-500 flex flex-wrap gap-3 justify-between items-center">
          <span data-footer-copy>© ${escapeHtml(copy.footer)}</span>
          <div class="flex items-center gap-4">
            <button type="button" class="theme-toggle" data-theme-toggle aria-label="${escapeHtml(copy.switchToDarkTheme)}" aria-pressed="false">
              <span class="theme-toggle-icon theme-toggle-sun" aria-hidden="true">☀</span>
              <span class="theme-toggle-icon theme-toggle-moon" aria-hidden="true">☾</span>
            </button>
            <a href="${privacyPathForLanguage(currentLanguage())}" class="hover:text-slate-300" data-privacy-link>${escapeHtml(copy.privacyLink)}</a>
            <a href="#/settings" class="hover:text-slate-300" data-data-link>${escapeHtml(copy.dataLink)}</a>
          </div>
        </div>
      </footer>
      <div class="install-modal" data-install-modal hidden>
        <button type="button" class="install-modal-backdrop" data-install-close aria-label="${escapeHtml(copy.close)}"></button>
        <section class="install-dialog" role="dialog" aria-modal="true" aria-labelledby="install-dialog-title">
          <div class="flex items-start justify-between gap-4 mb-4">
            <div>
              <h2 id="install-dialog-title" class="text-xl font-bold" data-install-title>${escapeHtml(copy.install.saveHint)}</h2>
              <p class="mt-2 text-sm leading-relaxed text-slate-400" data-install-lead></p>
            </div>
            <button type="button" class="install-close" data-install-close aria-label="${escapeHtml(copy.close)}">×</button>
          </div>
          <ol class="install-steps" data-install-steps></ol>
        </section>
      </div>
      <div class="analytics-consent" data-analytics-consent hidden>
        <section class="analytics-consent-panel" aria-labelledby="analytics-consent-title">
          <div class="min-w-0">
            <h2 id="analytics-consent-title" class="text-xs font-semibold text-slate-300" data-analytics-title>${escapeHtml(copy.analyticsTitle)}</h2>
            <p class="mt-0.5 text-[11px] leading-snug text-slate-500">
              <span data-analytics-lead>${escapeHtml(copy.analyticsLead)}</span>
              <a href="#/settings" class="text-slate-400 underline decoration-slate-700 underline-offset-2 hover:text-brand" data-details-link>${escapeHtml(copy.details)}</a>
            </p>
          </div>
          <div class="analytics-consent-actions">
            <button type="button" class="analytics-accept" data-analytics-accept>${escapeHtml(copy.accept)}</button>
          </div>
        </section>
      </div>
    </div>
  `;

  setupThemeSwitch(root);
  setupInstallAction(root);
  setupAnalyticsConsent(root);
  setupLanguageSelect(root);
  syncAppChrome(root);
  initMetrika();

  const view = root.querySelector<HTMLElement>('#view')!;
  const reportRoute = () => window.setTimeout(trackPageView, 0);

  const router = () => {
    applyPreferredLanguageRoute();
    syncAppChrome(root);
    syncLanguageSelect(root);
    const hash = location.hash.replace(/^#\/?/, '');
    if (hash === 'privacy') {
      setPrivacyMeta();
      void renderPrivacyRoute(view);
      reportRoute();
      return;
    }
    if (hash === 'settings') {
      setToolMeta(uiCopy().dataLink, 'Local data settings for PDF911.');
      void renderSettingsRoute(view);
      reportRoute();
      return;
    }
    if (hash) {
      const tool = findTool(hash);
      if (!tool) {
        setHomeMeta();
        renderHome(view, currentHomePage());
        reportRoute();
        return;
      }
      setToolMeta(tool.title, tool.description);
      renderToolView(view, tool);
      reportRoute();
      return;
    }

    if (isPrivacyPath(location.pathname)) {
      setPrivacyMeta();
      void renderPrivacyRoute(view);
      reportRoute();
      return;
    }

    const seoPage = getSeoPageForCurrentPath();
    if (seoPage) {
      const tool = findTool(seoPage.appHash);
      if (tool) {
        void renderSeoToolRoute(view, tool, seoPage);
        reportRoute();
        return;
      }
    }

    setHomeMeta();
    renderHome(view, currentHomePage());
    reportRoute();
  };

  window.addEventListener('hashchange', router);
  window.addEventListener('popstate', router);
  root.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const link = target.closest<HTMLAnchorElement>('a[href]');
    if (!link || event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const url = new URL(link.href, location.href);
    if (url.origin !== location.origin || url.hash) return;
    if (url.pathname !== '/' && !getSeoPageForPath(url.pathname) && !getHomeSeoForPath(url.pathname) && !isPrivacyPath(url.pathname)) return;
    event.preventDefault();
    history.pushState(null, '', url.pathname);
    router();
    window.scrollTo({ top: 0 });
  });
  router();
}

function normalizeRoutePath(pathname: string): string {
  return pathname.replace(/^\/+|\/+$/g, '');
}

function getSeoPageForCurrentPath(): SeoRoutePage | undefined {
  return getSeoPageForPath(location.pathname);
}

function getSeoPageForPath(pathname: string): SeoRoutePage | undefined {
  const normalized = normalizeRoutePath(pathname);
  if (!normalized) return undefined;
  return seoRoutePages.find((page) => page.slug === normalized);
}

function getHomeSeoForPath(pathname: string): HomeSeoPage | undefined {
  const normalized = normalizeRoutePath(pathname);
  return homeSeoPages.find((page) => page.slug === normalized);
}

function currentHomePage(): HomeSeoPage {
  return getHomeSeoForPath(location.pathname) ?? DEFAULT_HOME;
}

function currentLanguage(): string {
  return getSeoPageForCurrentPath()?.lang ?? getPrivacyPageForCurrentPath()?.lang ?? currentHomePage().lang;
}

function languageSelectorPages(): HomeSeoPage[] {
  const rank = new Map(LANGUAGE_SELECTOR_ORDER.map((lang, index) => [lang, index]));
  return [...homeSeoPages].sort((a, b) => (rank.get(a.lang) ?? 999) - (rank.get(b.lang) ?? 999));
}

function homePathForLanguage(lang: string): string {
  const supportedLang = matchSupportedLanguage(lang) ?? DEFAULT_LANG;
  const home = homeSeoPages.find((page) => page.lang === supportedLang) ?? DEFAULT_HOME;
  return `/${home.slug ? `${home.slug}/` : ''}`;
}

function homeHrefForLanguage(lang: string): string {
  return `${SITE_ORIGIN}${homePathForLanguage(lang)}`;
}

function privacyPageForLanguage(lang: string): PrivacySeoPage {
  const supportedLang = matchSupportedLanguage(lang) ?? DEFAULT_LANG;
  return privacySeoPages.find((page) => page.lang === supportedLang)
    ?? privacySeoPages.find((page) => page.lang === DEFAULT_LANG)
    ?? privacySeoPages[0];
}

function privacyPathForLanguage(lang: string): string {
  const page = privacyPageForLanguage(lang);
  return `/${page.slug ? `${page.slug}/` : ''}`;
}

function getPrivacyPageForCurrentPath(): PrivacySeoPage | undefined {
  return getPrivacyPageForPath(location.pathname);
}

function getPrivacyPageForPath(pathname: string): PrivacySeoPage | undefined {
  const normalized = normalizeRoutePath(pathname);
  if (!normalized) return undefined;
  return privacySeoPages.find((page) => page.slug === normalized);
}

function isPrivacyPath(pathname: string): boolean {
  return Boolean(getPrivacyPageForPath(pathname));
}

function isLikelyBot(): boolean {
  return BOT_USER_AGENT_RE.test(navigator.userAgent || '');
}

function isLanguageAutoRouteEligible(): boolean {
  if (location.hash || isPrivacyPath(location.pathname)) return false;
  const normalized = normalizeRoutePath(location.pathname);
  if (!normalized) return true;
  const seoPage = getSeoPageForPath(location.pathname);
  if (seoPage) return seoPage.lang === DEFAULT_LANG;
  const homePage = getHomeSeoForPath(location.pathname);
  if (homePage) return homePage.lang === DEFAULT_LANG;
  return false;
}

function applyPreferredLanguageRoute(): boolean {
  if (isLikelyBot() || !isLanguageAutoRouteEligible()) return false;

  const storedLanguage = getStoredLanguage();
  const preferredLanguage = storedLanguage ?? detectDeviceLanguage();
  if (preferredLanguage === currentLanguage()) return false;
  if (!storedLanguage && preferredLanguage === DEFAULT_LANG) return false;

  const nextPath = localizedPathForLanguage(preferredLanguage);
  const currentPath = location.pathname.endsWith('/') ? location.pathname : `${location.pathname}/`;
  if (nextPath === currentPath) return false;

  history.replaceState(null, '', `${nextPath}${location.search}`);
  return true;
}

function setHomeMeta(): void {
  const home = currentHomePage();
  const canonical = `${SITE_ORIGIN}/${home.slug ? `${home.slug}/` : ''}`;
  setDocumentMeta({
    title: home.title,
    description: home.description,
    canonical,
    lang: home.lang,
    locale: home.locale,
    alternates: home.alternates,
  });
  setRouteStructuredData(homeStructuredData(home));
}

function setPrivacyMeta(): void {
  const page = getPrivacyPageForCurrentPath() ?? privacyPageForLanguage(currentLanguage());
  setDocumentMeta({
    title: page.title,
    description: page.description,
    canonical: `${SITE_ORIGIN}/${page.slug}/`,
    lang: page.lang,
    locale: page.locale,
    alternates: page.alternates,
  });
  setRouteStructuredData([
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: page.h1,
      url: `${SITE_ORIGIN}/${page.slug}/`,
      inLanguage: page.lang,
      description: page.description,
      publisher: {
        '@type': 'Organization',
        name: BRAND_NAME,
      },
    },
  ]);
}

function setSeoMeta(page: SeoPage): void {
  setDocumentMeta({
    title: page.title,
    description: page.description,
    canonical: `${SITE_ORIGIN}/${page.slug}/`,
    lang: page.lang,
    locale: page.locale,
    alternates: page.alternates,
  });
  setRouteStructuredData(toolStructuredData(page));
}

function setSeoRouteMeta(page: SeoRoutePage): void {
  setDocumentMeta({
    title: page.title,
    description: page.description,
    canonical: `${SITE_ORIGIN}/${page.slug}/`,
    lang: page.lang,
    locale: page.locale,
    alternates: [],
  });
  setRouteStructuredData(null);
}

function setToolMeta(title: string, description: string): void {
  setDocumentMeta({
    title: `${title} — ${BRAND_NAME}`,
    description,
    canonical: `${SITE_ORIGIN}/`,
    lang: currentLanguage(),
    locale: currentHomePage().locale,
    alternates: currentHomePage().alternates,
  });
  setRouteStructuredData(null);
}

function setDocumentMeta(next: { title: string; description: string; canonical: string; lang: string; locale: string; alternates: SeoAlternate[] }): void {
  document.title = next.title;
  document.documentElement.lang = next.lang;
  document.documentElement.dir = languageDirection(next.lang);
  setMeta('description', next.description);
  setMeta('twitter:title', next.title);
  setMeta('twitter:description', next.description);
  setPropertyMeta('og:locale', next.locale);
  setPropertyMeta('og:title', next.title);
  setPropertyMeta('og:description', next.description);
  setPropertyMeta('og:url', next.canonical);
  let canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement('link');
    canonical.rel = 'canonical';
    document.head.appendChild(canonical);
  }
  canonical.href = next.canonical;
  setAlternateLinks(next.alternates);
}

function setMeta(name: string, content: string): void {
  const el = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (el) el.content = content;
}

function setPropertyMeta(property: string, content: string): void {
  const el = document.querySelector<HTMLMetaElement>(`meta[property="${property}"]`);
  if (el) el.content = content;
}

function setAlternateLinks(alternates: SeoAlternate[]): void {
  document.querySelectorAll<HTMLLinkElement>('link[rel="alternate"]').forEach((link) => link.remove());
  for (const alternate of alternates) {
    const link = document.createElement('link');
    link.rel = 'alternate';
    link.hreflang = alternate.hreflang;
    link.href = alternate.href;
    document.head.appendChild(link);
  }
}

function setRouteStructuredData(data: Array<Record<string, unknown>> | null): void {
  const id = 'pdf911-route-structured-data';
  let script = document.querySelector<HTMLScriptElement>(`#${id}`);
  if (!data) {
    script?.remove();
    return;
  }
  if (!script) {
    script = document.createElement('script');
    script.id = id;
    script.type = 'application/ld+json';
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(data).replace(/</g, '\\u003c');
}

function homeStructuredData(home: HomeSeoPage): Array<Record<string, unknown>> {
  const url = `${SITE_ORIGIN}/${home.slug ? `${home.slug}/` : ''}`;
  return [
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: BRAND_NAME,
      url,
      inLanguage: home.lang,
      description: home.description,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebApplication',
      name: BRAND_NAME,
      url,
      inLanguage: home.lang,
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Any',
      isAccessibleForFree: true,
      description: home.description,
      featureList: tools.map((tool) => tool.title),
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD',
      },
      publisher: {
        '@type': 'Organization',
        name: BRAND_NAME,
      },
    },
  ];
}

function toolStructuredData(page: SeoPage): Array<Record<string, unknown>> {
  const url = `${SITE_ORIGIN}/${page.slug}/`;
  const copy = uiCopy(page.lang);
  const keywords = [
    ...page.primaryKeywords,
    ...(page.enhancement?.variants ?? []),
  ];
  return [
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: copy.pdfTools,
          item: homeHrefForLanguage(page.lang),
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: page.h1,
          item: url,
        },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebApplication',
      name: page.h1,
      url,
      inLanguage: page.lang,
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Any',
      isAccessibleForFree: true,
      description: page.description,
      keywords: [...new Set(keywords)].join(', '),
      featureList: page.steps,
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD',
      },
      publisher: {
        '@type': 'Organization',
        name: BRAND_NAME,
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'HowTo',
      name: page.h1,
      description: page.lead,
      inLanguage: page.lang,
      totalTime: 'PT1M',
      step: page.steps.map((step, index) => ({
        '@type': 'HowToStep',
        position: index + 1,
        text: step,
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      inLanguage: page.lang,
      mainEntity: page.faqs.map(([question, answer]) => ({
        '@type': 'Question',
        name: question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: answer,
        },
      })),
    },
  ];
}

function syncAppChrome(root: HTMLElement): void {
  const lang = currentLanguage();
  const copy = uiCopy(lang);
  const homeLink = root.querySelector<HTMLAnchorElement>('[data-home-link]');
  if (homeLink) homeLink.href = homePathForLanguage(lang);

  const languageSelect = root.querySelector<HTMLSelectElement>('[data-language-select]');
  if (languageSelect) languageSelect.setAttribute('aria-label', copy.languageLabel);

  const footer = root.querySelector<HTMLElement>('[data-footer-copy]');
  if (footer) footer.textContent = `© ${copy.footer}`;

  const privacy = root.querySelector<HTMLAnchorElement>('[data-privacy-link]');
  if (privacy) {
    privacy.textContent = copy.privacyLink;
    privacy.href = privacyPathForLanguage(lang);
  }

  const data = root.querySelector<HTMLElement>('[data-data-link]');
  if (data) data.textContent = copy.dataLink;

  root.querySelectorAll<HTMLElement>('[data-install-close]').forEach((close) => {
    close.setAttribute('aria-label', copy.close);
  });

  const analyticsTitle = root.querySelector<HTMLElement>('[data-analytics-title]');
  if (analyticsTitle) analyticsTitle.textContent = copy.analyticsTitle;

  const analyticsLead = root.querySelector<HTMLElement>('[data-analytics-lead]');
  if (analyticsLead) analyticsLead.textContent = copy.analyticsLead;

  const details = root.querySelector<HTMLElement>('[data-details-link]');
  if (details) details.textContent = copy.details;

  const accept = root.querySelector<HTMLElement>('[data-analytics-accept]');
  if (accept) accept.textContent = copy.accept;

  const installTitle = root.querySelector<HTMLElement>('[data-install-title]');
  if (installTitle && root.querySelector<HTMLElement>('[data-install-modal]')?.hidden) {
    installTitle.textContent = copy.install.saveHint;
  }

  const installButton = root.querySelector<HTMLButtonElement>('[data-install-app]');
  const installLabel = root.querySelector<HTMLElement>('[data-install-label]');
  if (installButton && installLabel) {
    syncInstallButton(installButton, installLabel, lang);
  }

  const themeToggle = root.querySelector<HTMLButtonElement>('[data-theme-toggle]');
  if (themeToggle) syncThemeToggle(themeToggle, lang);
}

function syncThemeToggle(toggle: HTMLButtonElement, lang = currentLanguage()): void {
  const copy = uiCopy(lang);
  const activeTheme = getTheme();
  const isDark = activeTheme === 'dark';
  toggle.setAttribute('aria-pressed', String(isDark));
  toggle.setAttribute('aria-label', isDark ? copy.switchToLightTheme : copy.switchToDarkTheme);
  toggle.title = isDark ? copy.lightTheme : copy.darkTheme;
}

function setupThemeSwitch(root: HTMLElement) {
  const toggle = root.querySelector<HTMLButtonElement>('[data-theme-toggle]');
  if (!toggle) return;

  toggle.addEventListener('click', () => {
    setTheme(getTheme() === 'dark' ? 'light' : 'dark');
  });

  window.addEventListener('pdf911:theme-change', () => syncThemeToggle(toggle));
  syncThemeToggle(toggle);
}

function isStandaloneDisplay(): boolean {
  return window.matchMedia('(display-mode: standalone)').matches ||
    window.matchMedia('(display-mode: fullscreen)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

function syncInstallButton(button: HTMLButtonElement, label: HTMLElement, lang = currentLanguage()): void {
  if (isStandaloneDisplay()) {
    button.hidden = true;
    return;
  }
  const copy = uiCopy(lang);
  const platform = detectInstallPlatform();
  const text = deferredInstallPrompt ? copy.install.install : platform === 'ios' ? copy.install.homeScreen : copy.install.save;
  const hint = deferredInstallPrompt
    ? copy.install.installHint
    : platform === 'ios'
      ? copy.install.iosHint
      : copy.install.saveHint;
  button.hidden = false;
  label.textContent = text;
  button.title = hint;
  button.setAttribute('aria-label', hint);
}

function setupInstallAction(root: HTMLElement) {
  const button = root.querySelector<HTMLButtonElement>('[data-install-app]');
  const label = root.querySelector<HTMLElement>('[data-install-label]');
  const modal = root.querySelector<HTMLElement>('[data-install-modal]');
  const title = root.querySelector<HTMLElement>('[data-install-title]');
  const lead = root.querySelector<HTMLElement>('[data-install-lead]');
  const steps = root.querySelector<HTMLOListElement>('[data-install-steps]');
  if (!button || !label || !modal || !title || !lead || !steps) return;

  const sync = () => syncInstallButton(button, label);

  const showHelp = () => {
    const content = installHelpContent();
    title.textContent = content.title;
    lead.textContent = content.lead;
    steps.innerHTML = content.steps.map((step) => `<li>${escapeHtml(step)}</li>`).join('');
    modal.hidden = false;
    document.body.classList.add('install-modal-open');
    modal.querySelector<HTMLButtonElement>('[data-install-close]')?.focus();
  };

  const closeHelp = () => {
    modal.hidden = true;
    document.body.classList.remove('install-modal-open');
    button.focus();
  };

  button.addEventListener('click', async () => {
    if (deferredInstallPrompt) {
      const promptEvent = deferredInstallPrompt;
      deferredInstallPrompt = null;
      await promptEvent.prompt();
      try {
        await promptEvent.userChoice;
      } catch {
        // Some Chromium builds reject userChoice when the prompt is interrupted.
      }
      sync();
      return;
    }
    showHelp();
  });

  root.querySelectorAll<HTMLElement>('[data-install-close]').forEach((closeButton) => {
    closeButton.addEventListener('click', closeHelp);
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !modal.hidden) closeHelp();
  });

  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    deferredInstallPrompt = event as BeforeInstallPromptEvent;
    sync();
  });
  window.addEventListener('appinstalled', () => {
    deferredInstallPrompt = null;
    sync();
  });
  window.matchMedia('(display-mode: standalone)').addEventListener('change', sync);
  sync();
}

function setupAnalyticsConsent(root: HTMLElement) {
  const banner = root.querySelector<HTMLElement>('[data-analytics-consent]');
  const accept = root.querySelector<HTMLButtonElement>('[data-analytics-accept]');
  if (!banner || !accept) return;

  const sync = () => {
    const visible = isMetrikaHost() && getAnalyticsConsent() === null;
    banner.hidden = !visible;
  };

  accept.addEventListener('click', () => {
    setAnalyticsConsent('accepted');
    sync();
    initMetrika();
  });

  window.addEventListener('pdf911:analytics-consent-change', sync);
  sync();
}

function setupLanguageSelect(root: HTMLElement) {
  const select = root.querySelector<HTMLSelectElement>('[data-language-select]');
  if (!select) return;
  select.addEventListener('change', () => {
    const targetLang = matchSupportedLanguage(select.value) ?? DEFAULT_LANG;
    setStoredLanguage(targetLang);
    const nextPath = localizedPathForLanguage(targetLang);
    history.pushState(null, '', nextPath);
    window.dispatchEvent(new PopStateEvent('popstate'));
    window.scrollTo({ top: 0 });
  });
  syncLanguageSelect(root);
}

function syncLanguageSelect(root: HTMLElement) {
  const select = root.querySelector<HTMLSelectElement>('[data-language-select]');
  if (select) select.value = currentLanguage();
}

function localizedPathForLanguage(lang: string): string {
  const supportedLang = matchSupportedLanguage(lang) ?? DEFAULT_LANG;
  const currentSeo = getSeoPageForCurrentPath();
  if (currentSeo) {
    const nextSeo = seoRoutePages.find((page) => page.appHash === currentSeo.appHash && page.lang === supportedLang)
      ?? seoRoutePages.find((page) => page.appHash === currentSeo.appHash && page.lang === DEFAULT_LANG);
    return nextSeo ? `/${nextSeo.slug}/` : '/';
  }
  if (getPrivacyPageForCurrentPath()) return privacyPathForLanguage(supportedLang);
  const nextHome = homeSeoPages.find((page) => page.lang === supportedLang) ?? DEFAULT_HOME;
  return `/${nextHome.slug ? `${nextHome.slug}/` : ''}`;
}

function detectInstallPlatform(): 'ios' | 'android' | 'desktop' {
  const userAgent = navigator.userAgent || '';
  const platform = navigator.platform || '';
  const isTouchMac = platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  if (/iphone|ipad|ipod/i.test(userAgent) || isTouchMac) return 'ios';
  if (/android/i.test(userAgent)) return 'android';
  return 'desktop';
}

function installHelpContent(): { title: string; lead: string; steps: string[] } {
  const copy = uiCopy(currentLanguage()).install;
  const platform = detectInstallPlatform();
  if (platform === 'ios') {
    return {
      title: copy.iosTitle,
      lead: copy.iosLead,
      steps: copy.iosSteps,
    };
  }
  if (platform === 'android') {
    return {
      title: copy.androidTitle,
      lead: copy.androidLead,
      steps: copy.androidSteps,
    };
  }

  const shortcut = /mac/i.test(navigator.platform || '') ? 'Cmd + D' : 'Ctrl + D';
  return {
    title: copy.desktopTitle,
    lead: copy.desktopLead,
    steps: copy.desktopSteps(shortcut),
  };
}

function renderHome(view: HTMLElement, home: HomeSeoPage = currentHomePage()) {
  applyFullScreen(false);
  view.className = 'flex-1 w-full';

  const copy = uiCopy(home.lang);
  const filters = homeToolFilters(copy);
  const homeTools = orderedHomeTools();

  view.innerHTML = `
    <div class="max-w-6xl mx-auto px-4 py-8 sm:py-10">
      <div class="mb-8 grid gap-6 lg:grid-cols-[1fr_22rem] lg:items-center">
        <div>
          <h1 class="max-w-3xl text-4xl font-extrabold leading-tight tracking-normal sm:text-5xl">${escapeHtml(copy.homeHeadline)}</h1>
          <p class="mt-5 max-w-2xl text-lg leading-relaxed text-slate-400">${escapeHtml(copy.heroLead)}</p>
        </div>
        <div class="privacy-card">
          <div class="privacy-card-icon" aria-hidden="true">
            ${securityIconSvg()}
          </div>
          <div>
            <div class="font-semibold text-slate-200 mb-1">${escapeHtml(copy.privateTitle)}</div>
            <p>${escapeHtml(copy.privateText)}</p>
          </div>
        </div>
      </div>

      <div id="hero-drop" class="mb-5"></div>
      <div id="hero-result" class="mb-7"></div>

      <div id="home-tools">
        <section>
          <div class="mb-5 flex flex-col gap-3">
            <div>
              <h2 data-home-tools-title class="text-sm font-semibold text-slate-400 uppercase tracking-wider">${escapeHtml(copy.pdfTools)}</h2>
            </div>
            <div data-tool-filters class="-mx-4 flex gap-3 overflow-x-auto px-4 pb-1 sm:mx-0 sm:flex-wrap sm:px-0" role="group" aria-label="${escapeHtml(copy.toolFilterLabel)}">
              ${filters.map((filter) => toolFilterButtonHtml(filter)).join('')}
            </div>
          </div>
          <div id="tool-grid" class="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            ${homeTools.map((t) => toolCardHtml(t)).join('')}
          </div>
        </section>
      </div>

      ${homeHowItWorksHtml(copy)}
      <p class="mt-7 flex items-center justify-center gap-2 text-center text-sm text-slate-500">
        <span class="inline-icon inline-flex h-5 w-5 items-center justify-center text-brand" aria-hidden="true">${lockIconSvg()}</span>
        <span>${escapeHtml(copy.homeTrustFooter)}</span>
      </p>
    </div>
  `;

  const toolController = setupHomeToolFilters(view);
  const dropHost = view.querySelector<HTMLElement>('#hero-drop')!;
  const resultHost = view.querySelector<HTMLElement>('#hero-result')!;
  setupHeroDrop(dropHost, resultHost, toolController);
  const selectedPdf = peekSelectedFiles().find((file) => file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf'));
  if (selectedPdf) {
    toolController.setPendingPdf(selectedPdf);
    renderPdfFileState(dropHost, selectedPdf, () => {
      clearSelectedFiles();
      resultHost.innerHTML = '';
      toolController.clearPendingFile();
      setupHeroDrop(dropHost, resultHost, toolController);
    }, (nextFiles) => handleHeroFiles(nextFiles, dropHost, resultHost, toolController));
  }
}

function setupHeroDrop(
  dropHost: HTMLElement,
  resultHost: HTMLElement,
  toolController: HomeToolController,
) {
  const copy = uiCopy();
  dropHost.onclick = null;
  dropHost.innerHTML = `
    <div id="hero-zone" class="upload-zone">
      <div class="mx-auto flex max-w-2xl flex-col items-center text-center">
        <div class="upload-zone-icon mb-4">${cloudUploadIconSvg()}</div>
        <div class="text-2xl font-bold leading-tight text-slate-100">${escapeHtml(copy.dropFile)}</div>
        <div class="mt-3 text-sm text-slate-500">${escapeHtml(copy.fileTypes)}</div>
        <button id="hero-pick" class="btn-primary mt-6 text-base px-6 py-3">
          <span class="h-5 w-5" aria-hidden="true">${folderIconSvg()}</span>
          <span>${escapeHtml(copy.chooseFile)}</span>
        </button>
        <div class="mt-5 inline-flex items-center gap-2 text-sm text-slate-500">
          <span class="inline-icon inline-flex h-5 w-5 items-center justify-center text-brand" aria-hidden="true">${lockIconSvg()}</span>
          <span>${escapeHtml(copy.privacyChip)}</span>
        </div>
      </div>
      <input id="hero-input" type="file" class="hidden" multiple />
    </div>
  `;
  const zone = dropHost.querySelector<HTMLElement>('#hero-zone')!;
  const input = dropHost.querySelector<HTMLInputElement>('#hero-input')!;
  const pickBtn = dropHost.querySelector<HTMLButtonElement>('#hero-pick')!;

  pickBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    input.click();
  });
  zone.addEventListener('click', () => input.click());
  input.addEventListener('change', () => {
    const files = Array.from(input.files ?? []);
    if (files.length) handleHeroFiles(files, dropHost, resultHost, toolController);
    input.value = '';
  });
  ['dragenter', 'dragover'].forEach((ev) =>
    zone.addEventListener(ev, (e) => {
      e.preventDefault();
      zone.classList.add('border-brand', 'bg-slate-900');
    }),
  );
  ['dragleave', 'drop'].forEach((ev) =>
    zone.addEventListener(ev, (e) => {
      e.preventDefault();
      zone.classList.remove('border-brand', 'bg-slate-900');
    }),
  );
  zone.addEventListener('drop', (e) => {
    const files = Array.from(e.dataTransfer?.files ?? []);
    if (files.length) handleHeroFiles(files, dropHost, resultHost, toolController);
  });
}

function homeHowItWorksHtml(copy: UiCopy): string {
  const steps = [
    { n: '1', text: copy.howStepChoose },
    { n: '2', text: copy.howStepProcess },
    { n: '3', text: copy.howStepDownload },
  ];
  return `
    <section class="home-process-card" aria-label="${escapeHtml(copy.howItWorks)}">
      <div class="font-semibold text-slate-200">${escapeHtml(copy.howItWorks)}</div>
      <div class="home-process-steps">
        ${steps.map((step, index) => `
          <div class="home-process-step">
            <span class="home-process-number">${step.n}</span>
            <span>${escapeHtml(step.text)}</span>
          </div>
          ${index < steps.length - 1 ? '<span class="home-process-arrow" aria-hidden="true">→</span>' : ''}
        `).join('')}
      </div>
      <div class="home-process-safe">
        <span class="inline-flex h-8 w-8 shrink-0 items-center justify-center text-brand" aria-hidden="true">${securityIconSvg()}</span>
        <span>${escapeHtml(copy.howStepSecure)}</span>
      </div>
    </section>
  `;
}

function cloudUploadIconSvg(): string {
  return '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 18a5 5 0 0 1 .8-9.9A6.5 6.5 0 0 1 20 10.5 4 4 0 0 1 19 18h-2"/><path d="M12 12v8"/><path d="m8.5 15.5 3.5-3.5 3.5 3.5"/></svg>';
}

function folderIconSvg(): string {
  return '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 7.5h6l2 2h10v8.5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7.5Z"/><path d="M3 7.5V6a2 2 0 0 1 2-2h4l2 2h5a2 2 0 0 1 2 2v1.5"/></svg>';
}

function downloadIconSvg(): string {
  return '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3v12"/><path d="m7 10 5 5 5-5"/><path d="M5 21h14"/></svg>';
}

function lockIconSvg(): string {
  return '<svg viewBox="0 0 24 24" aria-hidden="true"><rect x="6" y="10" width="12" height="10" rx="2"/><path d="M9 10V7a3 3 0 0 1 6 0v3"/></svg>';
}

function securityIconSvg(): string {
  return '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3 20 6v5c0 5-3.3 8.2-8 10-4.7-1.8-8-5-8-10V6l8-3Z"/><path d="M9 12.5 11 14.5 15.5 10"/></svg>';
}

function handleHeroFiles(
  files: File[],
  dropHost: HTMLElement,
  resultHost: HTMLElement,
  toolController: HomeToolController,
) {
  trackFileSelected(files);
  const route = routeFiles(files);
  if (route.kind === 'route') {
    clearSelectedFiles();
    toolController.clearPendingFile();
    setPendingFiles(files);
    location.hash = `#/${route.toolId}`;
    return;
  }
  if (route.kind === 'choose-pdf-action') {
    resultHost.innerHTML = '';
    setSelectedFiles([route.file]);
    toolController.setPendingPdf(route.file);
    renderPdfFileState(dropHost, route.file, () => {
      clearSelectedFiles();
      resultHost.innerHTML = '';
      toolController.clearPendingFile();
      setupHeroDrop(dropHost, resultHost, toolController);
    }, (nextFiles) => handleHeroFiles(nextFiles, dropHost, resultHost, toolController));
    return;
  }
  clearSelectedFiles();
  toolController.clearPendingFile();
  setupHeroDrop(dropHost, resultHost, toolController);
  resultHost.innerHTML = `
    <div class="rounded-lg border border-amber-900/40 bg-amber-950/20 p-4 text-sm text-amber-300">
      ${escapeHtml(route.details)}
    </div>
  `;
}

function renderPdfFileState(
  host: HTMLElement,
  file: File,
  onCancel: () => void,
  onReplace: (files: File[]) => void,
) {
  const copy = uiCopy();
  host.innerHTML = `
    <div class="selected-file-card">
      <div class="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div class="flex min-w-0 items-start gap-3">
          <span class="grid h-12 w-12 shrink-0 place-items-center rounded-lg bg-brand text-xs font-extrabold text-white">PDF</span>
          <div class="min-w-0">
            <div class="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">${escapeHtml(copy.selectedFile)}</div>
            <div class="text-lg font-bold leading-snug text-slate-100 break-words">${escapeHtml(file.name)}</div>
            <div class="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-500">
              <span>${formatFileSize(file.size)}</span>
              <span data-pdf-pages>${escapeHtml(copy.countingPages)}</span>
            </div>
          </div>
        </div>
        <div class="flex flex-wrap gap-2 lg:justify-end">
          <button id="pdf-replace" class="btn-secondary text-xs px-3 py-1.5">${escapeHtml(copy.replaceFile)}</button>
          <button id="pdf-cancel" class="btn-secondary text-xs px-3 py-1.5">${escapeHtml(copy.removeFile)}</button>
          <input id="pdf-replace-input" type="file" class="hidden" multiple />
        </div>
      </div>
      <div class="mt-4 border-t border-slate-800 pt-4">
        <div class="text-base font-semibold text-slate-100">${escapeHtml(copy.detectedPdf)}</div>
        <div class="mt-1 text-sm text-slate-500">${escapeHtml(copy.choosePdfTool)}</div>
      </div>
    </div>
  `;
  const replaceInput = host.querySelector<HTMLInputElement>('#pdf-replace-input')!;
  host.onclick = (e) => {
    const target = e.target as HTMLElement;
    if (target.closest('#pdf-replace')) {
      replaceInput.click();
      return;
    }
    if (target.closest('#pdf-cancel')) onCancel();
  };
  replaceInput.addEventListener('change', () => {
    const files = Array.from(replaceInput.files ?? []);
    if (files.length) onReplace(files);
    replaceInput.value = '';
  });
  void updatePdfActionMetadata(host, file);
}

async function updatePdfActionMetadata(host: HTMLElement, file: File) {
  const pagesEl = host.querySelector<HTMLElement>('[data-pdf-pages]');
  if (!pagesEl) return;
  try {
    const [{ fileToBytes }, { openForRender }] = await Promise.all([
      import('./core/pdf-engine'),
      import('./core/render'),
    ]);
    const doc = await openForRender((await fileToBytes(file)).slice());
    pagesEl.textContent = `· ${formatPageCount(doc.numPages)}`;
    await doc.destroy();
  } catch {
    pagesEl.textContent = uiCopy().pageCountUnavailable;
  }
}

type HomeToolFilter = {
  id: 'all' | ToolMeta['category'];
  label: string;
  count?: number;
  icon?: string;
};
type HomeToolController = {
  setPendingPdf: (file: File) => void;
  clearPendingFile: () => void;
};

const homeCategoryOrder: ToolMeta['category'][] = ['pages', 'optimize', 'convert', 'edit', 'security'];
const homeToolOrder = [
  'merge',
  'compress',
  'pdf-to-docx',
  'jpg-to-pdf',
  'organize',
  'split',
  'rotate',
  'sign',
  'pdf-to-jpg',
  'pdf-to-txt',
  'docx-to-pdf',
  'xlsx-to-pdf',
  'edit',
  'csv-to-pdf',
];
const pdfFileToolIds = new Set([
  'compress',
  'organize',
  'merge',
  'pdf-to-docx',
  'sign',
  'edit',
  'split',
  'rotate',
  'pdf-to-jpg',
  'pdf-to-txt',
]);

function orderedHomeTools(): ToolMeta[] {
  const byId = new Map(tools.map((tool) => [tool.id, tool]));
  return [
    ...homeToolOrder.map((id) => byId.get(id)).filter((tool): tool is ToolMeta => Boolean(tool)),
    ...tools.filter((tool) => !homeToolOrder.includes(tool.id)),
  ];
}

function homeToolFilters(copy = uiCopy()): HomeToolFilter[] {
  const filters: HomeToolFilter[] = [
    { id: 'all', label: copy.allTools, count: tools.length, icon: 'all' },
  ];
  for (const category of ['pages', 'convert', 'edit', 'optimize'] as ToolMeta['category'][]) {
    const count = tools.filter((tool) => tool.category === category).length;
    if (!count) continue;
    filters.push({
      id: category,
      label: category === 'optimize' ? copy.compressFilter : copy.categories[category],
      count,
      icon: category,
    });
  }
  return filters;
}

function toolFilterButtonHtml(filter: HomeToolFilter): string {
  return `
    <button type="button" class="tool-filter" data-tool-filter="${filter.id}" aria-pressed="${filter.id === 'all'}">
      ${filterIconHtml(filter.icon)}
      <span>${escapeHtml(filter.label)}</span>
      ${typeof filter.count === 'number' ? `<span class="tool-filter-count">${filter.count}</span>` : ''}
    </button>
  `;
}

function filterIconHtml(icon?: string): string {
  const icons: Record<string, string> = {
    pages: '<path d="M7 3h7l4 4v14H7V3Z"/><path d="M14 3v5h5"/><path d="M10 13h6"/><path d="M10 17h6"/>',
    convert: '<path d="M7 7h10"/><path d="m14 4 3 3-3 3"/><path d="M17 17H7"/><path d="m10 14-3 3 3 3"/>',
    edit: '<path d="m4 20 4.5-1 10-10a2.2 2.2 0 0 0-3.1-3.1l-10 10L4 20Z"/><path d="m13.5 7.5 3 3"/>',
    optimize: '<path d="M12 4v12"/><path d="m7 11 5 5 5-5"/><path d="M5 20h14"/>',
    all: '<path d="M4 4h6v6H4V4Z"/><path d="M14 4h6v6h-6V4Z"/><path d="M4 14h6v6H4v-6Z"/><path d="M14 14h6v6h-6v-6Z"/>',
  };
  const path = icons[icon ?? ''] ?? icons.all;
  return `<svg class="tool-filter-icon" viewBox="0 0 24 24" aria-hidden="true">${path}</svg>`;
}

function setupHomeToolFilters(view: HTMLElement): HomeToolController {
  const buttons = Array.from(view.querySelectorAll<HTMLButtonElement>('[data-tool-filter]'));
  const cards = Array.from(view.querySelectorAll<HTMLElement>('[data-tool-card]'));
  const filtersHost = view.querySelector<HTMLElement>('[data-tool-filters]');
  const title = view.querySelector<HTMLElement>('[data-home-tools-title]');
  let pendingPdf: File | null = null;
  let currentFilter = 'all';

  const applyFilter = (filter: string) => {
    currentFilter = pendingPdf ? 'all' : filter;
    for (const button of buttons) {
      button.setAttribute('aria-pressed', String(button.dataset.toolFilter === currentFilter));
    }
    for (const card of cards) {
      const matchesFilter =
        currentFilter === 'all' ||
        card.dataset.toolCategory === currentFilter;
      const matchesPendingFile = !pendingPdf || card.dataset.pdfFileTool === 'true';
      const visible = matchesFilter && matchesPendingFile;
      card.classList.toggle('hidden', !visible);
    }
  };

  for (const button of buttons) {
    button.addEventListener('click', () => applyFilter(button.dataset.toolFilter ?? 'all'));
  }

  view.addEventListener('click', (event) => {
    if (!pendingPdf || event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const target = event.target;
    if (!(target instanceof Element)) return;
    const card = target.closest<HTMLElement>('[data-tool-card]');
    if (!card || card.dataset.pdfFileTool !== 'true') return;
    setPendingFiles([pendingPdf]);
  });

  const syncMode = () => {
    const copy = uiCopy();
    const active = Boolean(pendingPdf);
    filtersHost?.classList.toggle('hidden', active);
    if (title) title.textContent = active ? copy.detectedPdf : copy.pdfTools;
    applyFilter(active ? 'all' : currentFilter);
  };

  syncMode();

  return {
    setPendingPdf(file: File) {
      pendingPdf = file;
      syncMode();
    },
    clearPendingFile() {
      pendingPdf = null;
      syncMode();
    },
  };
}

function toolCardHtml(t: ToolMeta): string {
  const display = localizedToolDisplay(t);
  return `
    <a data-tool-card data-tool-category="${t.category}" data-tool-id="${t.id}" data-pdf-file-tool="${pdfFileToolIds.has(t.id)}" href="${toolHref(t.id)}" class="tool-card group min-h-[118px]">
      ${toolIconHtml(t)}
      <div class="min-w-0 flex-1">
        <div class="text-lg font-bold leading-snug transition-colors group-hover:text-brand">${escapeHtml(display.title)}${toolBadgeHtml(t)}</div>
        <div class="mt-2 text-sm text-slate-400 leading-relaxed">${escapeHtml(display.description)}</div>
      </div>
      <span class="tool-card-arrow" aria-hidden="true">›</span>
    </a>
  `;
}

function localizedToolDisplay(t: ToolMeta, lang = currentLanguage()): { title: string; description: string } {
  const page = seoRoutePages.find((item) => item.appHash === t.id && item.lang === lang);
  if (!page) return { title: t.title, description: t.description };
  return {
    title: page.lang === DEFAULT_LANG ? t.title : page.h1,
    description: page.lang === DEFAULT_LANG ? t.description : page.description,
  };
}

function toolHref(toolId: string): string {
  const lang = currentLanguage();
  const page = seoRoutePages.find((item) => item.appHash === toolId && item.lang === lang)
    ?? seoRoutePages.find((item) => item.appHash === toolId && item.lang === DEFAULT_LANG)
    ?? seoRoutePages.find((item) => item.appHash === toolId);
  return page ? `/${page.slug}/` : `#/${toolId}`;
}

function toolBadgeHtml(t: ToolMeta): string {
  return t.beta
    ? ' <span class="align-middle rounded border border-amber-800/70 bg-amber-950/30 px-1.5 py-0.5 text-[10px] uppercase text-amber-300">beta</span>'
    : '';
}

function toolIconHtml(t: ToolMeta, size: 'sm' | 'md' = 'md'): string {
  const box = size === 'sm' ? 'tool-visual tool-visual-sm' : 'tool-visual';
  const visual = toolVisual(t.id);
  return `
    <div class="${box} ${visual.tone}" aria-hidden="true">
      ${visual.icon}
    </div>
  `;
}

function toolVisual(toolId: string): { tone: string; icon: string } {
  const textIcon = (label: string) => `<span class="tool-visual-text">${escapeHtml(label)}</span>`;
  const svg = (paths: string) => `<svg viewBox="0 0 24 24" aria-hidden="true">${paths}</svg>`;
  const visuals: Record<string, { tone: string; icon: string }> = {
    merge: {
      tone: 'bg-violet-50 text-violet-600',
      icon: svg('<path d="M7 4h8l3 3v10H7V4Z"/><path d="M15 4v4h4"/><path d="M4 9h7l3 3-3 3H4"/>'),
    },
    compress: {
      tone: 'bg-emerald-50 text-emerald-700',
      icon: svg('<path d="M12 4v10"/><path d="m8 10 4 4 4-4"/><path d="M5 19h14"/><path d="M7 16v3"/><path d="M17 16v3"/>'),
    },
    'pdf-to-docx': {
      tone: 'bg-blue-50 text-blue-700',
      icon: textIcon('W'),
    },
    'jpg-to-pdf': {
      tone: 'bg-orange-50 text-orange-600',
      icon: svg('<path d="M5 5h14v14H5V5Z"/><path d="m7.5 16 3.5-4 2.5 3 2-2.5L18 16"/><circle cx="9" cy="9" r="1.3"/>'),
    },
    organize: {
      tone: 'bg-violet-50 text-violet-600',
      icon: svg('<path d="M5 4h6v7H5V4Z"/><path d="M13 4h6v4h-6V4Z"/><path d="M13 10h6v10h-6V10Z"/><path d="M5 13h6v7H5v-7Z"/>'),
    },
    split: {
      tone: 'bg-rose-50 text-rose-600',
      icon: svg('<path d="M6 5 18 19"/><path d="M18 5 6 19"/><circle cx="6" cy="5" r="2"/><circle cx="18" cy="5" r="2"/><circle cx="6" cy="19" r="2"/><circle cx="18" cy="19" r="2"/>'),
    },
    rotate: {
      tone: 'bg-teal-50 text-teal-700',
      icon: svg('<path d="M18 8a7 7 0 1 0 1 7"/><path d="M18 3v5h-5"/>'),
    },
    sign: {
      tone: 'bg-amber-50 text-amber-600',
      icon: svg('<path d="m5 19 4.2-1 9.3-9.3a2 2 0 0 0-2.8-2.8L6.4 15.2 5 19Z"/><path d="M13.5 7.5 16.5 10.5"/><path d="M4 21h16"/>'),
    },
    'pdf-to-jpg': {
      tone: 'bg-blue-50 text-blue-600',
      icon: svg('<path d="M5 5h14v14H5V5Z"/><path d="m7 16 3-3.5 2.2 2.4 1.7-2.1L17 16"/><circle cx="9" cy="9" r="1.2"/>'),
    },
    'pdf-to-txt': {
      tone: 'bg-purple-50 text-purple-700',
      icon: textIcon('T'),
    },
    'docx-to-pdf': {
      tone: 'bg-sky-50 text-sky-700',
      icon: textIcon('D'),
    },
    'xlsx-to-pdf': {
      tone: 'bg-emerald-50 text-emerald-700',
      icon: textIcon('X'),
    },
    edit: {
      tone: 'bg-rose-50 text-rose-600',
      icon: svg('<path d="m5 19 4.5-1 9.5-9.5a2.1 2.1 0 0 0-3-3L6.5 15 5 19Z"/><path d="M13.5 7.5 16.5 10.5"/>'),
    },
    'csv-to-pdf': {
      tone: 'bg-teal-50 text-teal-700',
      icon: textIcon('CSV'),
    },
  };
  return visuals[toolId] ?? {
    tone: 'bg-slate-100 text-slate-600',
    icon: textIcon(findTool(toolId)?.icon ?? 'PDF'),
  };
}

function seoSupportHtml(page: SeoPage): string {
  const enhancement = page.enhancement;
  const copy = uiCopy(page.lang);

  return `
    <section class="mt-12 border-t border-slate-800 pt-8 space-y-4" aria-label="${escapeHtml(copy.details)}">
      <div class="grid lg:grid-cols-3 gap-4">
        <div class="card">
          <h2 class="text-lg font-semibold mb-2">${escapeHtml(copy.howItWorks)}</h2>
          <ol class="list-decimal pl-5 text-sm text-slate-400 space-y-1">
            ${page.steps.map((step) => `<li>${escapeHtml(step)}</li>`).join('')}
          </ol>
        </div>
        <div class="card lg:col-span-2">
          <h2 class="text-lg font-semibold mb-2">${escapeHtml(copy.faq)}</h2>
          <div class="grid sm:grid-cols-3 gap-3">
            ${page.faqs.map(([q, a]) => `
              <div>
                <h3 class="text-sm font-semibold leading-snug mb-1">${escapeHtml(q)}</h3>
                <p class="text-xs leading-relaxed text-slate-500">${escapeHtml(a)}</p>
              </div>
            `).join('')}
          </div>
        </div>
      </div>
      ${enhancement ? `
        <div class="card">
          <h2 class="text-lg font-semibold mb-2">${escapeHtml(enhancement.heading)}</h2>
          <p class="text-sm leading-relaxed text-slate-400 max-w-4xl">${escapeHtml(enhancement.text)}</p>
          <div class="grid md:grid-cols-2 gap-5 mt-4">
            <div>
              <h3 class="text-sm font-semibold mb-2">${escapeHtml(copy.commonTasks)}</h3>
              <ul class="list-disc pl-5 text-sm text-slate-400 space-y-1">
                ${enhancement.tasks.map((task) => `<li>${escapeHtml(task)}</li>`).join('')}
              </ul>
            </div>
            <div>
              <h3 class="text-sm font-semibold mb-2">${escapeHtml(copy.popularSearches)}</h3>
              <div class="flex flex-wrap gap-2">
                ${enhancement.variants.map((query) => `<span class="rounded-full border border-slate-800 bg-slate-900/50 px-3 py-1 text-xs text-slate-400">${escapeHtml(query)}</span>`).join('')}
              </div>
            </div>
          </div>
        </div>
      ` : ''}
      <div class="card">
        <div class="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2 mb-4">
          <div>
            <h2 class="text-lg font-semibold">${escapeHtml(copy.morePdfTools)}</h2>
            <p class="mt-1 text-xs text-slate-500">${escapeHtml(copy.morePdfToolsLead)}</p>
          </div>
          <a href="${homePathForLanguage(page.lang)}" class="text-sm text-slate-400 hover:text-brand">${escapeHtml(copy.allTools)}</a>
        </div>
        <div class="grid items-start gap-4 md:grid-cols-2 xl:grid-cols-4">
          ${relatedToolGroupsHtml(page.slug, page.lang, copy)}
        </div>
      </div>
    </section>
  `;
}

function relatedToolGroupsHtml(currentSlug: string, lang = currentLanguage(), copy = uiCopy(lang)): string {
  return homeCategoryOrder
    .map((category) => {
      const items = tools.filter((tool) => tool.category === category && toolHref(tool.id).startsWith('/'));
      if (!items.length) return '';
      return `
        <section class="rounded-lg border border-slate-800 bg-slate-900/40 p-3">
          <h3 class="mb-2 flex items-center justify-between gap-2 text-sm font-semibold">
            <span>${escapeHtml(copy.categories[category])}</span>
            <span class="text-xs text-slate-500">${items.length}</span>
          </h3>
          <div class="flex flex-col gap-1.5">
            ${items.map((tool) => relatedToolLinkHtml(tool, currentSlug, lang, copy)).join('')}
          </div>
        </section>
      `;
    })
    .join('');
}

function relatedToolLinkHtml(tool: ToolMeta, currentSlug: string, lang = currentLanguage(), copy = uiCopy(lang)): string {
  const href = toolHref(tool.id);
  const slug = href.replace(/^\/+|\/+$/g, '');
  const active = slug === currentSlug;
  const display = localizedToolDisplay(tool, lang);
  return `
    <a href="${href}" class="flex items-center gap-2 rounded-lg border px-2 py-2 text-sm transition-colors ${active ? 'border-brand/30 bg-brand/10 text-brand' : 'border-transparent text-slate-300 hover:bg-slate-900/60 hover:text-slate-300'}">
      <span class="grid h-7 w-7 shrink-0 place-items-center rounded border border-slate-800 bg-slate-900 text-xs font-semibold">${escapeHtml(tool.icon)}</span>
      <span class="min-w-0 flex-1 truncate">${escapeHtml(display.title)}</span>
      ${active ? `<span class="text-[11px] uppercase text-brand">${escapeHtml(copy.current)}</span>` : ''}
    </a>
  `;
}

async function renderSeoToolRoute(view: HTMLElement, tool: ToolMeta, routePage: SeoRoutePage): Promise<void> {
  try {
    const mod = await import('./generated/seo-pages');
    const page = mod.seoPages.find((item) => item.slug === routePage.slug);
    if (page) {
      setSeoMeta(page);
      await renderToolView(view, tool, page);
      return;
    }
  } catch (err) {
    console.warn('Unable to load full SEO page data', err);
  }

  setSeoRouteMeta(routePage);
  await renderToolView(view, tool);
}

async function renderToolView(view: HTMLElement, tool: ToolMeta, seoPage?: SeoPage) {
  const fullscreen = tool.id === 'edit' && !seoPage;
  const lang = seoPage?.lang ?? currentLanguage();
  const copy = uiCopy(lang);
  const display = localizedToolDisplay(tool, lang);
  const title = seoPage?.h1 ?? display.title;
  const description = seoPage?.lead ?? display.description;
  const backHref = homePathForLanguage(lang);
  const toolRootClass = seoPage && tool.id === 'edit'
    ? 'min-h-[720px] h-[calc(100vh-180px)] flex flex-col overflow-hidden rounded-lg border border-slate-800 bg-slate-900'
    : '';
  applyFullScreen(fullscreen);

  if (fullscreen) {
    view.className = 'tool-fullscreen';
    view.innerHTML = `
      <div class="px-4 pt-3 pb-2 flex items-center justify-between gap-3 border-b border-slate-800 shrink-0">
        <div class="flex items-center gap-3 min-w-0">
          <a href="${backHref}" class="text-sm text-slate-400 hover:text-slate-200 shrink-0">← ${escapeHtml(copy.allTools)}</a>
          <h1 class="text-base font-bold truncate">${escapeHtml(title)}${toolBadgeHtml(tool)}</h1>
        </div>
      </div>
      <div id="tool-root" class="flex-1 min-h-0 flex flex-col"></div>
    `;
  } else {
    view.className = 'flex-1 w-full';
    view.innerHTML = `
      <div class="max-w-6xl mx-auto px-4 py-8">
        <a href="${backHref}" class="text-sm text-slate-400 hover:text-slate-200 mb-4 inline-block">← ${escapeHtml(copy.allTools)}</a>
        <h1 class="text-2xl sm:text-3xl font-bold mb-2">${escapeHtml(title)}${toolBadgeHtml(tool)}</h1>
        <p class="text-slate-400 mb-6">${escapeHtml(description)}</p>
        <div id="tool-root" class="${toolRootClass}"></div>
        ${seoPage ? seoSupportHtml(seoPage) : ''}
      </div>
    `;
  }

  const toolRoot = view.querySelector<HTMLElement>('#tool-root')!;

  toolRoot.innerHTML = `
      <div class="${fullscreen ? 'h-full' : 'card'} flex items-center justify-center py-12">
        <div class="flex items-center gap-3 text-slate-400">
          <span class="spinner spinner-lg"></span>
        <span>${escapeHtml(copy.openingTool)}</span>
      </div>
    </div>
  `;

  try {
    const mod = await tool.load();
    toolRoot.innerHTML = '';
    await mod.render(toolRoot);
  } catch (err) {
    console.error(err);
    trackToolError(err, 'tool_load');
    toolRoot.innerHTML = `
      <div class="card border-red-900/60 bg-red-950/20">
        <div class="font-semibold text-red-400 mb-2">${escapeHtml(copy.loadToolError)}</div>
        <div class="text-sm text-slate-300 mb-3">${escapeHtml((err as Error).message)}</div>
        <div class="text-xs text-slate-500 mb-4">
          ${escapeHtml(copy.checkConnection)}
        </div>
        <button class="btn-secondary" id="retry">${escapeHtml(copy.retry)}</button>
      </div>
    `;
    toolRoot.querySelector<HTMLButtonElement>('#retry')?.addEventListener('click', () =>
      renderToolView(view, tool, seoPage),
    );
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

function formatPageCount(count: number): string {
  const copy = uiCopy();
  return `${count} ${count === 1 ? copy.pageSingular : copy.pagePlural}`;
}

function applyFullScreen(on: boolean) {
  const footer = document.querySelector<HTMLElement>('#app-footer');
  if (footer) footer.style.display = on ? 'none' : '';
  document.body.classList.toggle('overflow-hidden', on);
}

async function renderSettingsRoute(view: HTMLElement) {
  const copy = uiCopy();
  applyFullScreen(false);
  view.className = 'flex-1 w-full';
  view.innerHTML = `<div class="max-w-4xl mx-auto px-4 py-8 flex items-center gap-3 text-slate-400"><span class="spinner"></span><span>${escapeHtml(copy.openingSettings)}</span></div>`;
  try {
    const mod = await import('./pages/settings');
    await mod.render(view);
  } catch (err) {
    view.innerHTML = `<div class="max-w-4xl mx-auto px-4 py-8 text-red-400">${escapeHtml(copy.settingsLoadError)} ${escapeHtml((err as Error).message)}</div>`;
  }
}

async function renderPrivacyRoute(view: HTMLElement) {
  const copy = uiCopy();
  applyFullScreen(false);
  view.className = 'flex-1 w-full';
  try {
    const mod = await import('./pages/privacy');
    await mod.render(view);
  } catch (err) {
    view.innerHTML = `<div class="max-w-4xl mx-auto px-4 py-8 text-red-400">${escapeHtml(copy.pageLoadError)} ${escapeHtml((err as Error).message)}</div>`;
  }
}
